import ReadModelInfo from "./ReadModelInfo";
import PartnerDirectoryDetails from "./PartnerDirectoryDetails";
import { useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Box, DialogActions, DialogContent, DialogTitle, LinearProgress, Link, Stack, Typography } from "@mui/material";
import OpenInNewRounded from "@mui/icons-material/OpenInNewRounded";
import { MonitorButton, MonitorDialog } from "../../components/MonitorControls";
import PromotionArtifactDeployments from "../../components/PromotionArtifactDeployments";
import DateLabel from "../../components/DateLabel";
import { v } from "../../utils/helpers";
import { stageLabel } from "../../utils/stages";
import { PdConflictSection } from "./PdConflictResolver";

const stageOf = (data, stage) => (data?.stages || []).find(entry => entry.stage === stage) || null;
const safeUrl = value => { try { return ["https:", "http:"].includes(new URL(value).protocol); } catch { return false; } };

/** iFlows einer Stufe, die auf Edge manuell deployt werden müssen (Vertrag 27.3, deployment.edgeManual). */
export function edgeManualArtifacts(entry, stage, targetStage) {
  const row = stageOf(entry?.data, stage);
  return [...(row?.artifacts || []), ...(row?.designArtifacts || [])].filter(artifact => (targetStage && artifact.deploymentByStage ? artifact.deploymentByStage.find(plan => plan.stage === targetStage) : artifact?.deployment)?.edgeManual === true);
}

/**
 * Edge-Hinweise „Edge: iFlow X manuell deployen“ für Pakete einer Quellstufe, etwa im Antrags- und Freigabedialog.
 * Der Freigebende braucht den Hinweis: Laden und Fehler bleiben sichtbar, ein Fehler bietet „Erneut laden“.
 */
export function EdgeHints({ details = {}, packageIds = [], stage, targetStage, onRetry, hideLoading = false }) {
  const { t } = useTranslation("freigabe");
  const loading = packageIds.some(packageId => !details[packageId] || details[packageId].loading);
  const failed = packageIds.filter(packageId => details[packageId]?.error || (!details[packageId]?.loading && details[packageId]?.data && !stageOf(details[packageId].data, stage)));
  const hints = packageIds.flatMap(packageId => edgeManualArtifacts(details[packageId], stage, targetStage)
    .map(artifact => ({ key: `${packageId}:${artifact.id}`, packageId, name: artifact.name || artifact.id })));
  // Mehrere Pakete (z. B. je Werk ein Paket): Hinweise je Paket gruppieren, damit klar ist, welches iFlow zu welchem Paket gehört.
  const grouped = packageIds.length > 1;
  const groups = packageIds.map(packageId => ({ packageId, label: details[packageId]?.data?.packageName || packageId,
    hints: hints.filter(hint => hint.packageId === packageId) })).filter(group => group.hints.length);
  // Fehlerbehandlung Werkssplit: dieselbe iFlow ID in mehreren Paketen des Antrags heißt, die Pakete sind nicht getrennt.
  const owners = new Map();
  if (grouped && !loading) for (const packageId of packageIds) {
    for (const artifact of stageOf(details[packageId]?.data, stage)?.artifacts || []) {
      const list = owners.get(artifact.id) || [];
      if (!list.includes(packageId)) owners.set(artifact.id, [...list, packageId]);
    }
  }
  const shared = [...owners].filter(([, list]) => list.length > 1).map(([id]) => id);
  return <>
    {packageIds.filter(id => details[id]?.data?.readModel).map(id => <ReadModelInfo key={id}
      label={grouped ? details[id].data.packageName || id : t("stageBranches.details.freshness")}
      message={t(details[id].data.readModel.state === "stale" ? "stageBranches.readModel.stale" : "stageBranches.details.savedNotes")}
      metadata={details[id].data.readModel}/>)}
    {loading && !hideLoading && <Stack spacing={0.5}>
      <Typography variant="body2" sx={{ color: v("text-2") }}>{t("stageBranches.details.edgeLoading")}</Typography>
      <LinearProgress aria-label={t("stageBranches.details.edgeLoading")}/>
    </Stack>}
    {failed.length > 0 && <Alert severity="warning" variant="outlined"
      action={onRetry && <MonitorButton size="small" variant="text" onClick={() => failed.forEach(packageId => onRetry(packageId))}>
        {t("stageBranches.details.retry")}</MonitorButton>}>
      {t("stageBranches.details.edgeUnavailable")}
    </Alert>}
    {shared.length > 0 && <Alert severity="error" variant="outlined" role="alert">
      {t("stageBranches.details.sharedArtifacts", { ids: shared.join(", ") })}
    </Alert>}
    {hints.length > 0 && <Alert severity="info" variant="outlined">
      {grouped ? <Stack spacing={1}>
        {groups.map(group => <Box key={group.packageId}>
          <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: "anywhere" }}>{group.label}</Typography>
          <Stack component="ul" spacing={0.25} sx={{ m: 0, pl: 2 }}>
            {group.hints.map(hint => <li key={hint.key} style={{ overflowWrap: "anywhere" }}>{t("stageBranches.details.edgeHint", { name: hint.name })}</li>)}
          </Stack>
        </Box>)}
      </Stack> : <Stack component="ul" spacing={0.25} sx={{ m: 0, pl: 2 }}>
        {hints.map(hint => <li key={hint.key} style={{ overflowWrap: "anywhere" }}>{t("stageBranches.details.edgeHint", { name: hint.name })}</li>)}
      </Stack>}
    </Alert>}
  </>;
}

/**
 * Details einer Paketkarte auf Anfrage (Vertrag 27.3): beim Öffnen des Dialogs über onLoad geladen. Zeigt die Artefakte
 * mit Deploymentabsicht, die Läufe der Stufe mit Link und die Edge-Hinweise je iFlow.
 */
export default function PackageDetails({ entry, stage, packageId, packageName, artifactIds = [], onLoad, pdConflict = null, targetStageLabel, stageLabelFn }) {
  const { t } = useTranslation("freigabe");
  const panelId = useId();
  const trigger = useRef(null);
  const [open, setOpen] = useState(false);
  const show = () => { onLoad?.(packageId); setOpen(true); };
  const close = () => { setOpen(false); requestAnimationFrame(() => trigger.current?.focus()); };
  const stageEntry = stageOf(entry?.data, stage);
  const runs = stageEntry?.runs || [];
  const result = run => t(run.status !== "completed" ? "stageBranches.details.result.running"
    : run.conclusion === "success" ? "stageBranches.details.result.success" : "stageBranches.details.result.failure");

  return <Box sx={{ minWidth: 0 }}>
    <MonitorButton ref={trigger} variant="text" size="small" aria-haspopup="dialog" onClick={show}>{t("stageBranches.details.toggle")}</MonitorButton>
    {open && <MonitorDialog className="promotion-dialog sb-dialog" maxWidth="lg" aria-labelledby={panelId} onClose={close}>
      <DialogTitle id={panelId}>{t("stageBranches.details.toggle")}: {packageName || packageId}</DialogTitle>
      <DialogContent>
      <Stack spacing={2} sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ color: v("text-2"), overflowWrap: "anywhere" }}>{packageId} · {stageLabel(stage)}</Typography>
      {entry?.data?.readModel && <ReadModelInfo label={t("stageBranches.readModel.infoLabel")}
          message={t(entry.data.readModel.state === "stale" ? "stageBranches.readModel.stale" : "stageBranches.readModel.infoReady")}
          metadata={entry.data.readModel}/>}
        {pdConflict && <PdConflictSection pdConflict={pdConflict} targetStageLabel={targetStageLabel} stageLabelFn={stageLabelFn}/>}
      {entry?.loading && <LinearProgress aria-label={t("stageBranches.details.loading")}/>}
      {entry?.error && <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
        <Typography variant="body2" sx={{ color: v("warning"), minWidth: 0 }}>{t("stageBranches.details.unavailable")}</Typography>
        <MonitorButton size="small" variant="text" onClick={() => onLoad?.(packageId)}>{t("stageBranches.details.retry")}</MonitorButton>
      </Stack>}
      {entry?.data && <>
        {edgeManualArtifacts(entry, stage).map(artifact => <Typography key={artifact.id} variant="body2" sx={{ color: v("text-1"), overflowWrap: "anywhere" }}>
          {t("stageBranches.details.edgeHint", { name: artifact.name || artifact.id })}</Typography>)}
        <Box>
          <Typography component="h3" variant="body2" sx={{ color: v("text-2"), fontWeight: 600, m: 0 }}>{t("stageBranches.details.runs")}</Typography>
          {runs.length ? <Stack component="ul" spacing={0.5} sx={{ listStyle: "none", m: 0, p: 0 }}>
            {runs.map(run => <Typography component="li" variant="body2" key={`${run.runId}:${run.runAttempt}`} sx={{ color: v("text-1"), overflowWrap: "anywhere" }}>
              {safeUrl(run.runUrl) ? <Link href={run.runUrl} target="_blank" rel="noopener noreferrer" sx={{ color: v("accent") }}>
                {t("stageBranches.details.run", { runId: run.runId, stage: stageLabel(stage) })}<OpenInNewRounded sx={{ fontSize: 14, ml: 0.5, verticalAlign: "middle" }} aria-hidden="true"/></Link>
                : t("stageBranches.details.run", { runId: run.runId, stage: stageLabel(stage) })}
              {" · "}{result(run)}{run.completedAt && <>{" · "}<DateLabel value={run.completedAt}/></>}
            </Typography>)}
          </Stack> : <Typography variant="body2" sx={{ color: v("text-2") }}>{t("stageBranches.details.noRuns")}</Typography>}
        </Box>
        <Box component="section">
          <Typography component="h3" variant="subtitle2">{t("promotion.artifacts")}</Typography>
          <PromotionArtifactDeployments artifacts={stageEntry?.artifacts || []}
            artifactIds={stageEntry?.artifacts?.map(artifact => artifact.id) || artifactIds}
            designArtifacts={stageEntry?.designArtifacts}/>
          <PartnerDirectoryDetails data={stageEntry?.partnerDirectory}/>
        </Box>
      </>}
      </Stack>
      </DialogContent>
      <DialogActions><MonitorButton autoFocus onClick={close}>{t("gemeinsam:aktion.schliessen")}</MonitorButton></DialogActions>
    </MonitorDialog>}
  </Box>;
}
